import { motion } from 'framer-motion';
import type { IconType } from 'react-icons';
import { LuBook, LuBookmark, LuFileText, LuLayers } from 'react-icons/lu';
import type { CourseContext, MobileFilterMode } from '../../stores/domain.store';

interface MobileFilterPillsProps {
  activeFilter: MobileFilterMode;
  onFilterChange: (filter: MobileFilterMode) => void;
  context: CourseContext | null;
}

interface FilterOption {
  mode: MobileFilterMode;
  label: string;
  icon: IconType;
}

// Simple labels that fit on one line
const filters: FilterOption[] = [
  { mode: 'full', label: 'Course', icon: LuLayers },
  { mode: 'domain', label: 'Unit', icon: LuBook },
  { mode: 'path', label: 'Ch', icon: LuBookmark },
  { mode: 'lesson', label: 'Lesson', icon: LuFileText },
];

/**
 * The phone's Course / Unit / Ch / Lesson breadcrumb, as a segmented
 * control (redesign PR4, course.css). Same filters and disabled rules.
 */
export function MobileFilterPills({
  activeFilter,
  onFilterChange,
  context,
}: MobileFilterPillsProps) {
  // Disable filters that don't have context
  const isDisabled = (mode: MobileFilterMode): boolean => {
    if (mode === 'full') return false;
    if (mode === 'domain') return !context?.domainId;
    if (mode === 'path') return !context?.pathId;
    if (mode === 'lesson') return !context?.lessonId;
    return false;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="ee-course-filter"
    >
      {filters.map((filter) => {
        const disabled = isDisabled(filter.mode);
        const isActive = activeFilter === filter.mode;
        const Icon = filter.icon;

        return (
          <motion.button
            type="button"
            key={filter.mode}
            onClick={() => !disabled && onFilterChange(filter.mode)}
            whileTap={!disabled ? { scale: 0.95 } : undefined}
            disabled={disabled}
            aria-pressed={isActive}
            className="ee-course-filter__option"
          >
            <Icon aria-hidden="true" />
            <span>{filter.label}</span>
          </motion.button>
        );
      })}
    </motion.div>
  );
}
